import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { geoCentroid } from 'd3-geo';
import { scaleLinear } from 'd3-scale';
import axios from 'axios';
import CandidatoAvatar from '../../components/CandidatoAvatar';
import {
  BRAZIL_GEO_URL, STATE_NAMES, STATE_LABEL_OFFSETS, PARTY_COLORS, capitalize,
} from './constants';
import { useContainerSize } from './hooks';
import PoliticoHoverCard from './PoliticoHoverCard';
import AvatarTip from './AvatarTip';

export default function GameStateMap({ onSelect, governors, playerAvatar }) {
  const [hovered, setHovered] = useState(null);
  const [hoveredPres, setHoveredPres] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [popMap, setPopMap] = useState({});
  const [geoLoaded, setGeoLoaded] = useState(false);
  const [presidente, setPresidente] = useState(null);
  const containerRef = useRef(null);
  const { width: cw } = useContainerSize(containerRef);

  useEffect(() => {
    axios.get('/populacao/estados')
      .then(res => {
        if (res.data?.success) {
          const m = {};
          (res.data.data || []).forEach(e => { if (e.sigla) m[e.sigla] = e.populacao; });
          setPopMap(m);
        }
      })
      .catch(() => {});
    axios.get('/politicos/presidente')
      .then(res => { if (res.data?.success) setPresidente(res.data.data); })
      .catch(() => {});
  }, []);

  const popColorScale = useMemo(() => {
    const vals = Object.values(popMap);
    if (vals.length === 0) return null;
    const maxPop = Math.max(...vals, 1);
    return scaleLinear()
      .domain([0, maxPop * 0.05, maxPop * 0.2, maxPop * 0.5, maxPop])
      .range(['#1a1a2e', '#16213e', '#0f3460', '#e94560', '#ff6b6b']);
  }, [popMap]);

  const svgW = Math.max(400, cw || 600);
  const svgH = Math.round(svgW * 0.93);
  const scale = Math.round(svgW * 1.13);

  const gov = hovered ? governors[hovered] : null;
  const hoveredPop = hovered ? popMap[hovered] : null;

  const formatPop = (val) => {
    if (val >= 1e6) return `${(val / 1e6).toFixed(1)}M`;
    if (val >= 1e3) return `${(val / 1e3).toFixed(0)}mil`;
    return String(val);
  };

  return (
    <div className="state-map">
      {playerAvatar && (
        <AvatarTip
          avatar={playerAvatar.svg}
          text="Comece como assessor, ganhe influência e candidate-se na próxima eleição."
        />
      )}
      <div className="state-map__header">
        <h2 className="state-map__title">Escolha seu Estado</h2>
        {presidente && (
          <div className="state-map__presidente"
            onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}>
            {presidente.presidente && (
              <div className="state-map__presidente-item"
                style={{ cursor: 'default' }}
                onMouseEnter={() => setHoveredPres('presidente')}
                onMouseLeave={() => setHoveredPres(null)}>
                <CandidatoAvatar candidato={presidente.presidente} foto={presidente.presidente.foto} nome={presidente.presidente.nome} size={32} className="rounded-lg border border-white/10" />
                <div className="state-map__presidente-info">
                  <p className="text-xs font-bold leading-tight text-white">{capitalize(presidente.presidente.nome)} <span className="font-normal text-[10px] text-gray-400">· Presidente</span></p>
                  <p className="text-[10px] leading-tight text-gray-400">{presidente.presidente.partido}</p>
                </div>
              </div>
            )}
            {presidente.vicePresidente && (
              <div className="state-map__presidente-item"
                style={{ cursor: 'default' }}
                onMouseEnter={() => setHoveredPres('vice')}
                onMouseLeave={() => setHoveredPres(null)}>
                <CandidatoAvatar candidato={presidente.vicePresidente} foto={presidente.vicePresidente.foto} nome={presidente.vicePresidente.nome} size={28} className="rounded-lg border border-white/10" />
                <div className="state-map__presidente-info">
                  <p className="text-xs font-bold leading-tight text-white">{capitalize(presidente.vicePresidente.nome)} <span className="font-normal text-[10px] text-gray-400">· Vice</span></p>
                  <p className="text-[10px] leading-tight text-gray-400">{presidente.vicePresidente.partido}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <p className="state-map__subtitle">Cores indicam o tamanho da populacao — clique para escolher</p>

      {popColorScale && (
        <div className="state-map__legend">
          <span className="state-map__legend-label">Menor</span>
          <div className="state-map__legend-bar" />
          <span className="state-map__legend-label">Maior pop.</span>
        </div>
      )}

      <div ref={containerRef} className="state-map__container"
        onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}>
        {/* Loading indicator */}
        {!geoLoaded && (
          <div className="flex flex-col items-center justify-center py-20 sm:py-32">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-2 border-transparent border-t-[#e94560] border-r-[#e94560] mb-4" />
            <p className="text-sm text-gray-400">Carregando mapa do Brasil...</p>
            <p className="text-[10px] text-gray-600 mt-1">Isso pode levar alguns segundos</p>
          </div>
        )}
        {cw > 0 && (
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{ center: [-54, -15], scale }}
            width={svgW}
            height={svgH}
            style={{ width: '100%', height: 'auto', opacity: geoLoaded ? 1 : 0, transition: 'opacity 0.4s' }}
          >
            <Geographies geography={BRAZIL_GEO_URL}>
              {({ geographies }) => {
                if (geographies.length > 0 && !geoLoaded) {
                  setTimeout(() => setGeoLoaded(true), 0);
                }
                return geographies.map((geo) => {
                  const sigla = geo.properties.sigla;
                  const pop = popMap[sigla];
                  const baseFill = pop && popColorScale ? popColorScale(pop) : '#16213e';
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onClick={() => onSelect(sigla, STATE_NAMES[sigla] || sigla)}
                      onMouseEnter={() => setHovered(sigla)}
                      onMouseLeave={() => setHovered(null)}
                      style={{
                        default: {
                          fill: baseFill,
                          stroke: '#0a0e1a',
                          strokeWidth: 0.8,
                          cursor: 'pointer',
                          transition: 'fill 0.2s',
                        },
                        hover: { fill: '#ffffff', stroke: '#e94560', strokeWidth: 1.5, cursor: 'pointer' },
                        pressed: { fill: '#c73850', stroke: '#ffffff', strokeWidth: 1.2 },
                      }}
                    />
                  );
                });
              }}
            </Geographies>
            {/* Governor party badges on each state */}
            <Geographies geography={BRAZIL_GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const sigla = geo.properties.sigla;
                  const stateGov = governors[sigla];
                  if (!stateGov?.partido) return null;
                  const centroid = geoCentroid(geo);
                  const partyColor = PARTY_COLORS[stateGov.partido] || '#888';
                  const off = STATE_LABEL_OFFSETS[sigla] || [0, 0];
                  const isSmall = ['DF', 'SE', 'AL', 'RN'].includes(sigla);
                  const badgeR = isSmall ? svgW * 0.008 : svgW * 0.012;
                  const badgeFont = isSmall ? Math.max(svgW * 0.006, 4) : Math.max(svgW * 0.009, 5);
                  const hasOffset = off[0] !== 0 || off[1] !== 0;
                  return (
                    <React.Fragment key={`badge-${sigla}`}>
                      {hasOffset && (
                        <Marker coordinates={centroid}>
                          <line
                            x1={0} y1={0}
                            x2={off[0] * svgW * 0.06} y2={off[1] * svgW * 0.06}
                            stroke="rgba(255,255,255,.15)" strokeWidth={0.6} strokeDasharray="2,2"
                          />
                        </Marker>
                      )}
                      <Marker coordinates={[centroid[0] + off[0], centroid[1] + off[1]]}>
                        <circle r={badgeR} fill={partyColor} stroke="#0a0e1a" strokeWidth={1.2} opacity={0.92} />
                        <text
                          textAnchor="middle"
                          dominantBaseline="central"
                          className="state-map__party-text"
                          style={{ fontSize: `${badgeFont}px` }}
                        >
                          {stateGov.partido.length > 4 ? stateGov.partido.slice(0, 3) : stateGov.partido}
                        </text>
                      </Marker>
                    </React.Fragment>
                  );
                })
              }
            </Geographies>
          </ComposableMap>
        )}
      </div>

      {hovered && !gov && (
        <div className="state-map__hover-info">
          <p className="state-map__hover-name">
            {STATE_NAMES[hovered] || hovered}
          </p>
          {hoveredPop && (
            <p className="state-map__hover-pop">População: {new Intl.NumberFormat('pt-BR').format(hoveredPop)}</p>
          )}
        </div>
      )}

      {gov && (
        <PoliticoHoverCard
          politico={gov}
          label={`Governador${gov.genero === 'FEMININO' ? 'a' : ''} — ${STATE_NAMES[hovered] || hovered}`}
          position={mousePos}
          extraInfo={hoveredPop ? `Pop: ${formatPop(hoveredPop)}` : null}
        />
      )}

      {hoveredPres === 'presidente' && presidente?.presidente && (
        <PoliticoHoverCard
          politico={presidente.presidente}
          label="Presidente da República"
          position={mousePos}
        />
      )}
      {hoveredPres === 'vice' && presidente?.vicePresidente && (
        <PoliticoHoverCard
          politico={presidente.vicePresidente}
          label="Vice-Presidente"
          position={mousePos}
        />
      )}
    </div>
  );
}
